import React, { useEffect, useMemo, useState, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import withAuthContext from '../../context/auth/AuthConsumer';
import { IAuthContext } from '../../context/auth/auth-context';
import Sidebar from './Sidebar';
import './index.scss';
import DeepSearchInput from './PermitAgentInput';
import ChatWindow from './ChatWindow';
import { ChatResponse } from '../../types/deep-research';
import styled from 'styled-components';
import { permitAgentChat } from '../../services/firebase';
import { authFetch } from '../../services/authClient';
import { FileDropOverlay } from '../../components/ui';
import useFileDrop from '../../hooks/useFileDrop';
import { IMAGE_MIME_TYPES, partitionFiles } from '../../utils/fileAccept';
import notifyRejectedFiles from '../../utils/notifyRejectedFiles';
import { isExxonMobileEmail } from '../Validator';

const ContentRow = styled.div`
  display: flex;
  height: 100%;
`;

const ChatSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  padding: 16px 22px 20px;
`;

const DocumentsSection = styled.div`
  width: 400px;
  display: flex;
  flex-direction: column;
  background-color: #000000;
  border-radius: 8px;
  padding: 16px;
`;

const SectionTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 18px;
  font-weight: bold;
  color: #fff;
  margin-bottom: 16px;
`;

// const EmptyDocumentsBox = styled.div`
//   background-color: #18181b;
//   border: 1px solid #3a3a3a;
//   border-radius: 8px;
//   padding: 32px;
//   text-align: center;
//   color: #fff;
// `;

const DocumentCard = styled.div`
  background-color: #18181b;
  border: 1px solid #3a3a3a;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #212124;
  }
`;

const DocumentTitle = styled.div`
  color: #fff;
  font-weight: 600;
  margin-bottom: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const DocumentDate = styled.div`
  color: #fff;
  font-size: 12px;
`;

// const HeaderTitle = styled.div`
//   display: flex;
//   align-items: center;
//   gap: 12px;
//   font-size: 30px;
//   font-weight: 600;
//   color: #fff;
//   margin-bottom: 24px;
//   font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen', 'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
//   letter-spacing: -0.5px;
// `;

const TopBarWrapper = styled.div`
  width: auto;
  margin-bottom: 0;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 10px;
`;

const TopRail = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  width: 100%;
  margin-bottom: 8px;
`;

const TopBrand = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  line-height: 1.1;
  color: #d6e4e7;
`;

const TopBrandTitle = styled.div`
  font-size: 34px;
  font-weight: 500;
  letter-spacing: 0.01em;
`;

const TopBrandSub = styled.div`
  margin-top: 2px;
  font-size: 17px;
  color: #30d5b0;
  opacity: 0.9;
`;

const BrowseBox = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 7px 14px 7px 12px;
  border-radius: 999px;
  border: 1px solid rgba(52, 211, 153, 0.45);
  background-color: rgba(2, 7, 13, 0.88);
  color: #cbd5e1;
  font-size: 12px;
  flex-shrink: 0;
`;

const BrowseIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #f59e0b;
  font-size: 16px;
`;

const BrowseTitle = styled.span`
  font-weight: 500;
  color: #e2e8f0;
  letter-spacing: 0.01em;
`;

const BrowseBadge = styled.span`
  padding: 4px 10px;
  border-radius: 999px;
  background-color: rgba(52, 211, 153, 0.2);
  color: #d1fae5;
  font-size: 12px;
  font-weight: 500;
  margin-left: 2px;
`;

const RoutePicker = styled.span<{ disabled?: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  opacity: ${(p) => (p.disabled ? 0.7 : 1)};
`;

const SelectedRouteValue = styled.span`
  font-size: 12px;
  color: #94a3b8;
  opacity: 0.9;
`;

const RouteSelect = styled.select`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  cursor: pointer;
  z-index: 2;

  &:disabled {
    cursor: not-allowed;
  }

  option,
  optgroup {
    color: #f8fafc;
    background-color: #030712;
  }
`;

const BrowseHint = styled.span`
  font-size: 11px;
  color: #647787;
  letter-spacing: 0.02em;
`;

const HintText = styled.div`
  margin-top: 8px;
  font-size: 13px;
  color: #9e9e9e;
  text-align: center;
`;

const BottomInputWrapper = styled.div`
  width: 100%;
  max-width: 800px;
  margin: 0 auto 20px auto;
  flex-shrink: 0;
`;

const HeroEmptyState = styled.div`
  flex: 1;
  position: relative;
  min-height: 0;
  display: flex;
  flex-direction: column;
  width: 100%;
  border-radius: 0;
  overflow: hidden;
  background: #000;
`;

const HeroForeground = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 14px 14px;
  overflow-y: auto;
`;

/** Active chat: single scroll region is the message list, not the whole column */
const ChatStageForeground = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding: 14px 14px;
  overflow: hidden;
`;

const HeroMain = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  text-align: center;
`;

const HeroHeadingRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  gap: 12px;
  margin-bottom: 62px;
  margin-left: auto;
  margin-right: auto;
`;

const HeroLogoBadge = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: #24d3a3;
  color: #1f3340;
  font-size: 18px;
  font-weight: 500;
  letter-spacing: -0.04em;
  line-height: 1;
  text-transform: lowercase;
`;

const HeroHeading = styled.h1`
  margin: 0;
  font-size: clamp(36px, 3.2vw, 56px);
  line-height: 1.08;
  font-weight: 500;
  color: #fff;
  letter-spacing: 0.01em;
`;

const HeroInputBlock = styled.div`
  width: 100%;
  max-width: 680px;
  margin: 0 auto;
`;

const HeroHelperText = styled.p`
  margin: 10px 0 0;
  text-align: center;
  color: #667786;
  font-size: 12px;
  font-weight: 600;
`;

const SuggestionChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 14px;
  max-width: 760px;
`;

const UploadHelperChip = styled.button`
  appearance: none;
  border: 1px solid rgba(55, 208, 170, 0.42);
  background: rgba(12, 69, 64, 0.58);
  color: #34d9b6;
  font-size: 12px;
  padding: 8px 14px;
  border-radius: 999px;
  cursor: pointer;
  font-family: inherit;
  line-height: 1;
  backdrop-filter: blur(6px);
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: rgba(20, 86, 78, 0.72);
    border-color: rgba(94, 234, 212, 0.5);
  }
`;

const SuggestionChip = styled.button`
  appearance: none;
  border: 1px solid rgba(112, 132, 156, 0.28);
  background: rgba(5, 10, 16, 0.72);
  color: #8f9eaf;
  font-size: 12px;
  padding: 8px 14px;
  border-radius: 999px;
  cursor: pointer;
  font-family: inherit;
  backdrop-filter: blur(6px);
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: rgba(52, 211, 153, 0.12);
    border-color: rgba(52, 211, 153, 0.35);
  }
`;

const FileRetentionNote = styled.p`
  margin: 12px 0 0;
  text-align: center;
  color: #5f707f;
  font-size: 11px;
`;

const HERO_SUGGESTIONS = [
  'Prior permit',
  'Plot plan',
  'Equipment list',
  'Lab results',
  'Emission factors',
  'Emissions calcs',
];

const baseApiUrl = `${process.env.REACT_APP_ENVIROCHAT_API_ENDPOINT}/pbr/get_pred`;
const availablePermitsApiUrl = `${process.env.REACT_APP_ENVIROCHAT_API_ENDPOINT}/get_available_permits`;

type PermitOption = { code: string; name: string };
type AvailablePermitsResponse = { pbr?: PermitOption[]; sp?: PermitOption[] };

const PermitAgent: React.FC<{ context: IAuthContext }> = ({ context }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [chatData, setChatData] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [chatId, setChatId] = useState<number | string | null>(null);
  const [permitChatDocId, setPermitChatDocId] = useState<string | null>(null);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<any[]>([]);
  const [availablePermits, setAvailablePermits] = useState<AvailablePermitsResponse>({ pbr: [], sp: [] });
  const [isPermitsLoading, setIsPermitsLoading] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState<string>('');
  const textareaRef: any = useRef(null);
  const activeRequestControllerRef = useRef<AbortController | null>(null);
  const activeRequestIdRef = useRef<number>(0);
  const location = useLocation();
  const history = useHistory();

  const isRouteLocked = useMemo(() => {
    return Boolean(chatId) || chatData.length > 0 || isLoading;
  }, [chatId, chatData.length, isLoading]);

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const loadPermits = async () => {
      setIsPermitsLoading(true);
      try {
        const res = await authFetch(availablePermitsApiUrl, { signal: controller.signal });
        const data = await res.json() as AvailablePermitsResponse;
        if (!isMounted) return;
        setAvailablePermits({
          pbr: Array.isArray(data?.pbr) ? data.pbr : [],
          sp: Array.isArray(data?.sp) ? data.sp : [],
        });
      } catch (e) {
        // tslint:disable-next-line:no-console
        console.log('Error loading available permits', e);
        if (!isMounted) return;
        setAvailablePermits({ pbr: [], sp: [] });
      } finally {
        if (isMounted) {
          setIsPermitsLoading(false);
        }
      }
    };

    loadPermits();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    const q = new URLSearchParams(location.search).get('q');
    if (!q) { return; }
    setSearchText(decodeURIComponent(q));
    history.replace('/permit-agent');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Helper function to convert Blob to base64
  const blobToBase64 = (blob: any) => {
    return new Promise((resolve, reject) => {
      const reader: any = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };

  /**
   * Picker adapter. The input's `accept` filter is advisory — the file dialog
   * can be switched to "All files" — so validate here as well as on drop.
   * @param event
   * @returns
   */
  const uploadAttachment = async (event: any) => {
    if (!event || !event.target) { return; }
    const selectedFiles = Array.from(event.target.files) as File[];
    event.target.value = ''; // Reset the file input value to allow selecting the same file again
    if (!selectedFiles.length) {
      return;
    }
    const { accepted, rejected } = partitionFiles(selectedFiles);
    notifyRejectedFiles(rejected);
    await appendAttachmentFiles(accepted);
  };

  const appendAttachmentFiles = async (selectedFiles: File[]) => {
    if (!selectedFiles.length) {
      return;
    }

    const updatedFiles = [...uploadedFiles, ...selectedFiles];
    setUploadedFiles(updatedFiles);
    setFilePreviews([]); // Reset file previews

    // Process each file for previews
    const previews = await Promise.all(
      updatedFiles.map(async (file: File) => {
        if (IMAGE_MIME_TYPES.includes(file.type)) {
          const base64 = await blobToBase64(file);
          return { preview: base64, name: file.name, type: file.type };
        }
        return { preview: URL.createObjectURL(file), name: file.name, type: file.type };
      })
    );

    setFilePreviews(previews);
  };

  // Drop anywhere on the page to attach, matching the paperclip's own lockout.
  const { isDragging } = useFileDrop({
    onFiles: appendAttachmentFiles,
    onReject: notifyRejectedFiles,
    disabled: isLoading || isExxonMobileEmail(context),
  });

  /**
   * Remove attachment files in search input
   * @param index
   */
  const removeAttachment = (index: number) => {
    setUploadedFiles((prevFiles: File[]) => prevFiles.filter((_: File, i: number) => i !== index));
    setFilePreviews((prevPreviews: any[]) => prevPreviews.filter((_: any, i: number) => i !== index));
  };

  useEffect(() => {
    const stateFiles = (location.state as any)?.files as File[] | undefined;
    if (Array.isArray(stateFiles) && stateFiles.length) {
      void appendAttachmentFiles(stateFiles);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleStartNewChat = () => {
    setChatData([]);
    setChatId(null);
    setPermitChatDocId(null);
    setSearchText('');
    setSelectedRoute('');
  };

  /**
   * When loading a chat from history that does not yet have an external chat id,
   * submit the last user message to the external API to (re)create the external chat.
   * If externalChatId is provided, continue the existing conversation.
   */
  const submitPreviousMessageToExternal = async (previousMessage: string, chatDocId: string | null, externalChatId?: string | null) => {
    const messageText = (previousMessage || '').trim();
    if (!messageText) {
      return;
    }

    try {
      const chatStatus = externalChatId ? 'ongoing' : 'new';
      const localUserId = localStorage.getItem('uid');
      const cancelToken = new AbortController();

      const method = 'GET';
      let url = baseApiUrl;
      const fetchOptions: any = {
        method,
        signal: cancelToken.signal,
      };

      const q = encodeURIComponent(messageText);
      const userId = localUserId ? encodeURIComponent(localUserId) : '';

      const queryParams = new URLSearchParams({
        chatStatus,
        q,
      });

      if (userId) {
        queryParams.append('userId', userId);
      }
      if (externalChatId) {
        queryParams.append('chatId', externalChatId.toString());
      }

      url = `${baseApiUrl}?${queryParams.toString()}`;

      const response = await authFetch(url, fetchOptions);
      const data = await response.json() as ChatResponse;
      if (!data) {
        return;
      }

      setChatId(data.chat_id);

      const assistantMessage = {
        content: data.content || 'No response received.',
        role: 'assistant',
        status: data.status,
        pdf: data.pdf,
        file_urls: data.file_urls,
      };

      // Save assistant message & metadata to Firestore for this existing chat
      void permitAgentChat({
        type: 'create_or_append',
        chat_doc_id: chatDocId,
        role: 'assistant',
        content: assistantMessage.content,
        status: assistantMessage.status,
        pdf: assistantMessage.pdf,
        file_urls: assistantMessage.file_urls,
        external_chat_id: data.chat_id,
        permit_metadata: {
          permit_file_urls: data.file_urls || assistantMessage.file_urls || null,
          status: data.status,
        },
      }).catch((e: any) => {
        // tslint:disable-next-line:no-console
        console.log('Error saving assistant message (auto-submit) to Firestore', e);
      });

      // Append assistant message to the current chat view
      setChatData((prev) => [...prev, assistantMessage]);
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.log('Error submitting previous message to external API', error);
    }
  };

  const fetchResponse = async () => {
    const localSearchText = searchText.trim();
    if (localSearchText === '') return;

    // Increment request id for this external call
    const currentRequestId = ++activeRequestIdRef.current;

    // Add user message to chat
    const userMessage = {
      content: localSearchText,
      role: 'user',
      attachFiles: filePreviews || [],
    };
    setChatData((prev) => [...prev, userMessage]);

    // Clear input immediately for better UX
    setSearchText('');
    if (textareaRef.current) {
      textareaRef.current.style.height = '34px';
    }
    // Reset file previews from input after adding to message
    setFilePreviews([]);

    // Save user message to Firestore
    try {
      if (!permitChatDocId) {
        // First message in this chat: create the chat doc and first message
        const saveUserRes: any = await permitAgentChat({
          type: 'create_or_append',
          chat_doc_id: null,
          role: 'user',
          content: localSearchText,
          title: chatData.length === 0 ? localSearchText : undefined,
        });
        if (saveUserRes && saveUserRes.data && saveUserRes.data.chat_doc_id) {
          setPermitChatDocId(saveUserRes.data.chat_doc_id);
        }
      } else {
        // Existing chat: just append user message
        void permitAgentChat({
          type: 'create_or_append',
          chat_doc_id: permitChatDocId,
          role: 'user',
          content: localSearchText,
        }).catch((e: any) => {
          // tslint:disable-next-line:no-console
          console.log('Error saving user message to Firestore', e);
        });
      }
    } catch (e) {
      // tslint:disable-next-line:no-console
      console.log('Error creating chat and saving first user message', e);
    }
    setIsLoading(true);

    try {
      const chatStatus = chatId ? 'ongoing' : 'new';
      const routeToSend = chatStatus === 'new' && selectedRoute ? selectedRoute : '';
      const localUserId = localStorage.getItem('uid');
      // Cancel any in-flight external request before starting a new one
      if (activeRequestControllerRef.current) {
        activeRequestControllerRef.current.abort();
      }
      const cancelToken = new AbortController();
      activeRequestControllerRef.current = cancelToken;

      // Decide whether to use 'POST' or 'GET' based on the presence of files
      const method = Array.isArray(uploadedFiles) && uploadedFiles.length ? 'POST' : 'GET';

      let url = baseApiUrl;
      const fetchOptions: any = {
        method,
        signal: cancelToken.signal,
      };

      if (method === 'POST') {
        // Build FormData for POST requests
        const formData = new FormData();
        formData.append('q', localSearchText);
        formData.append('chatStatus', chatStatus);
        if (routeToSend) {
          formData.append('route', routeToSend);
        }

        if (localUserId) {
          formData.append('userId', encodeURIComponent(localUserId));
        }
        if (chatId) {
          formData.append('chatId', chatId.toString());
        }

        // Add files to FormData
        if (Array.isArray(uploadedFiles) && uploadedFiles.length) {
          uploadedFiles.forEach((file: File) => formData.append('files', file));
        }

        fetchOptions.body = formData;
        // Reset files after sending
        setUploadedFiles([]);
        setFilePreviews([]);
      } else {
        // Build query params for GET requests
        const q = encodeURIComponent(localSearchText);
        const userId = localUserId ? encodeURIComponent(localUserId) : '';

        const queryParams = new URLSearchParams({
          chatStatus,
          q,
        });
        if (routeToSend) {
          queryParams.append('route', routeToSend);
        }

        if (userId) {
          queryParams.append('userId', userId);
        }
        if (chatId) {
          queryParams.append('chatId', chatId.toString());
        }

        url = `${baseApiUrl}?${queryParams.toString()}`;
      }

      const response = await authFetch(url, fetchOptions);
      const data = await response.json() as ChatResponse;
      if (!data) {
        return;
      }

      setChatId(data.chat_id);
      const assistantMessage = {
        content: data.content || 'No response received.',
        role: 'assistant',
        status: data.status,
        pdf: data.pdf,
        file_urls: data.file_urls,
      };

      // Save assistant message & metadata to Firestore, using current chat_doc_id
      void permitAgentChat({
        type: 'create_or_append',
        chat_doc_id: permitChatDocId,
        role: 'assistant',
        content: assistantMessage.content,
        status: assistantMessage.status,
        pdf: assistantMessage.pdf,
        file_urls: assistantMessage.file_urls,
        external_chat_id: data.chat_id,
        permit_metadata: {
          permit_file_urls: data.file_urls || assistantMessage.file_urls || null,
          status: data.status,
        },
      })
        .then((saveAssistantRes: any) => {
          if (saveAssistantRes && saveAssistantRes.data && saveAssistantRes.data.chat_doc_id) {
            setPermitChatDocId(saveAssistantRes.data.chat_doc_id);
          }
        })
        .catch((e: any) => {
          // tslint:disable-next-line:no-console
          console.log('Error saving assistant message to Firestore', e);
        });

      setChatData((prev) => {
        const existingItem = prev.find((item) => item.status === 'executing');
        if (!existingItem) {
          return [...prev, assistantMessage];
        }
        existingItem.content = assistantMessage.content;
        existingItem.pdf = assistantMessage.pdf;
        existingItem.file_urls = assistantMessage.file_urls;
        return prev;
      });
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.log('Error fetching response:', error);
    } finally {
      // Only clear loading state if this is the latest request
      if (currentRequestId === activeRequestIdRef.current) {
        setIsLoading(false);
      }
    }
  };

  const loadChatFromHistory = async (selectedChatId: string) => {
    // selectedChatId is Firestore chat_doc_id
    // tslint:disable-next-line:no-console
    console.log('loadChatFromHistory called with chat_doc_id:', selectedChatId);
    setIsLoading(true);
    setChatData([]); // Clear existing chat data first

    try {
      const response: any = await permitAgentChat({
        type: 'get_chat_messages',
        chat_doc_id: selectedChatId,
      });

      if (!response || !response.data) {
        setChatData([{
          content: 'Unable to load chat history.',
          role: 'assistant',
          status: 'completed',
        }]);
        return;
      }

      const { messages, chat } = response.data;
      const loadedMessages = (messages || []).map((m: any) => ({
        content: m.content,
        role: m.role,
        status: m.status,
        pdf: m.pdf,
        file_urls: m.file_urls,
      }));

      setChatData(loadedMessages);
      setPermitChatDocId(chat.chat_id);
      setChatId(chat.external_chat_id || null);

      // Auto-submit last user message in two cases:
      // 1. When external_chat_id is not there (regardless of last message type)
      // 2. When last message is from user (regardless of external_chat_id)
      if (loadedMessages.length > 0) {
        const lastMessage = loadedMessages[loadedMessages.length - 1];
        const shouldAutoSubmit = !chat.external_chat_id || lastMessage.role === 'user';

        if (shouldAutoSubmit) {
          let messageToSubmit: string | null = null;
          let externalChatIdToUse: string | null = null;

          if (lastMessage.role === 'user' && lastMessage.content) {
            // Last message is from user - submit it, use external_chat_id if available
            messageToSubmit = lastMessage.content;
            externalChatIdToUse = chat.external_chat_id || null;
          } else if (!chat.external_chat_id) {
            // No external_chat_id - find the last user message
            const lastUserMessage = [...loadedMessages].reverse().find((m) => m.role === 'user');
            if (lastUserMessage && lastUserMessage.content) {
              messageToSubmit = lastUserMessage.content;
              externalChatIdToUse = null;
            }
          }

          if (messageToSubmit) {
            await submitPreviousMessageToExternal(messageToSubmit, chat.chat_id, externalChatIdToUse);
          }
        }
      }
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.log('Error loading chat from history:', error);
      setChatData([{
        content: 'Error loading chat history. Please try again.',
        role: 'assistant',
        status: 'completed',
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const getFileUrls = () => {
    const fileNames: { [key: string]: string } = {
      path_106_261: '106.261 Form',
      path_106_262: '106.262 Form',
      path_106_4: '106.4 Checklist',
      path_106_478: '106.478 Form',
      path_workbook: 'TCEQ Workbook',
      path_pi7: 'PI-7 Form',
      path_20396: 'Form 20396 (AQSPB Checklist)',
      path_table1a: 'Table 1(a) - Emission Point Summary',
      path_pi1s: 'PI-1S Form',
      path_sp_compliance: 'SP Compliance Form',
      path_boiler_sp_compliance: 'Boiler SP Compliance Form',
      path_nsr_psd_applicability: 'NSR/PSD Applicability Form',
      path_compiled: 'Compiled Package',
    };

    const lastAssistantMessage = [...chatData].reverse().find((item) => item.role === 'assistant');
    const fileUrls = lastAssistantMessage?.file_urls;
    if (!fileUrls || typeof fileUrls !== 'object') {
      return [];
    }

    return Object.entries(fileUrls)
      .filter(([, url]) => url && typeof url === 'string' && (url as string).trim() !== '')
      .map(([key, url]) => {
        const name = fileNames[key] || key.replace('path_', '').replace(/_/g, ' ').split(' ').map((word: string) =>
          word.charAt(0).toUpperCase() + word.slice(1)
        ).join(' ');
        return { key, url: url as string, name };
      });
  };

  const parseDateFromUrl = (url: string): Date => {
    const filename = url.split('/').pop()?.split('?')[0] ?? '';
    const match = filename.match(/_(\d{4})(\d{2})(\d{2})_(\d{2})(\d{2})(\d{2})/);
    if (match) {
      const [, year, month, day, hour, minute, second] = match;
      return new Date(Date.UTC(+year, +month - 1, +day, +hour, +minute, +second));
    }
    return new Date();
  };

  const files = getFileUrls();
  const hasDocuments = files.length > 0;

  const isHeroLanding = chatData.length === 0 && !isLoading;

  const browseAuthorizationsBar = (
    <TopBarWrapper>
      <BrowseBox>
        <BrowseIcon>
          <i className="bi bi-file-text" style={{ fontSize: '18px' }} />
        </BrowseIcon>
        <RoutePicker disabled={isRouteLocked || isPermitsLoading}>
          <BrowseTitle>Browse Authorizations</BrowseTitle>
          {selectedRoute ? <SelectedRouteValue>{selectedRoute}</SelectedRouteValue> : null}
          <i
            className="bi bi-chevron-down"
            style={{ fontSize: '12px', color: '#94a3b8', marginLeft: '2px', pointerEvents: 'none' }}
          />
          <RouteSelect
            value={selectedRoute}
            onChange={(e: any) => setSelectedRoute(e.target.value)}
            disabled={isRouteLocked || isPermitsLoading}
            title={
              isRouteLocked
                ? 'Route is locked after chat starts'
                : 'Select a permit route (optional)'
            }
          >
            <option value="">
              {isPermitsLoading ? 'Loading permits…' : 'No route selected'}
            </option>
            {Array.isArray(availablePermits?.pbr) && availablePermits.pbr.length > 0 && (
              <optgroup label="PBR">
                {availablePermits.pbr.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.name} ({p.code})
                  </option>
                ))}
              </optgroup>
            )}
            {Array.isArray(availablePermits?.sp) && availablePermits.sp.length > 0 && (
              <optgroup label="SP">
                {availablePermits.sp.map((p) => (
                  <option key={p.code} value={p.code}>
                    {p.name} ({p.code})
                  </option>
                ))}
              </optgroup>
            )}
          </RouteSelect>
        </RoutePicker>
        <BrowseBadge>
          {((availablePermits?.pbr?.length || 0) + (availablePermits?.sp?.length || 0))} automated
        </BrowseBadge>
      </BrowseBox>
      <BrowseHint>or describe what you need below ↓</BrowseHint>
    </TopBarWrapper>
  );

  return (
    <div className="permit-agent" style={{ height: '100vh', background: '#000000', overflow: 'hidden' }}>
      <FileDropOverlay visible={isDragging} />
      <Sidebar
        isHovered={isHovered}
        setIsHovered={setIsHovered}
        onLoadChat={loadChatFromHistory}
        activeChatId={chatId}
        onStartNewChat={handleStartNewChat}
      />
      <div style={{ marginLeft: 190, height: '100vh', display: 'flex', flexDirection: 'column' }}>
        <ContentRow>
          <ChatSection>
            {isHeroLanding ? (
              <HeroEmptyState>
                <HeroForeground>
                  <TopRail>
                    {browseAuthorizationsBar}
                    <TopBrand>
                      <TopBrandTitle>enviro.ai</TopBrandTitle>
                      <TopBrandSub>Permit Agent Pro 1.0</TopBrandSub>
                    </TopBrand>
                  </TopRail>
                  <HeroMain>
                    <HeroHeadingRow>
                      <HeroLogoBadge aria-hidden="true">ai</HeroLogoBadge>
                      <HeroHeading>Environmental permit agent</HeroHeading>
                    </HeroHeadingRow>
                    <HeroInputBlock>
                      <DeepSearchInput
                        value={searchText}
                        onChange={(e: any) => setSearchText(e.target.value)}
                        onEnter={() => fetchResponse()}
                        textareaRef={textareaRef}
                        hasDiscussionStarted={false}
                        variant="permit"
                        placeholder="What do you want to permit?"
                        uploadAttachment={uploadAttachment}
                        filePreviews={filePreviews}
                        removeAttachment={removeAttachment}
                        context={context}
                      />
                    </HeroInputBlock>
                    <HeroHelperText>Browse authorizations above or describe what you need</HeroHelperText>
                    <SuggestionChips aria-label="Suggested prompts">
                      <UploadHelperChip
                        type="button"
                        onClick={() => {
                          const inputEl = document.getElementById('file-input-permit-agent') as HTMLInputElement | null;
                          inputEl?.click();
                        }}
                      >
                        <i className="bi bi-cloud-upload" style={{ marginRight: 6 }} />
                        Drop files or click - anything helps the agent
                      </UploadHelperChip>
                      {HERO_SUGGESTIONS.map((label) => (
                        <SuggestionChip
                          key={label}
                          type="button"
                          onClick={() => {
                            setSearchText(label);
                            requestAnimationFrame(() => textareaRef.current?.focus());
                          }}
                        >
                          {label}
                        </SuggestionChip>
                      ))}
                    </SuggestionChips>
                    <FileRetentionNote>
                      <i className="bi bi-lock-fill" style={{ marginRight: 6 }} />
                      Files stay in your tenant - auto-purged after 30 days
                    </FileRetentionNote>
                  </HeroMain>
                </HeroForeground>
              </HeroEmptyState>
            ) : (
              <HeroEmptyState>
                <ChatStageForeground>
                  <TopRail>
                    {browseAuthorizationsBar}
                    <TopBrand>
                      <TopBrandTitle>enviro.ai</TopBrandTitle>
                      <TopBrandSub>Permit Agent Pro 1.0</TopBrandSub>
                    </TopBrand>
                  </TopRail>
                  <div
                    className="chat-on-hero"
                    style={{
                      flex: 1,
                      minHeight: 0,
                      overflowY: 'auto',
                      marginBottom: 16,
                    }}
                  >
                    {chatData.length ? (
                      <ChatWindow
                        chatData={chatData}
                        isLoading={isLoading}
                        onStartResearch={() => undefined}
                      />
                    ) : isLoading ? (
                      <div
                        style={{
                          padding: '20px',
                          backgroundColor: 'rgba(40, 42, 44, 0.92)',
                          borderRadius: '8px',
                          border: '1px solid rgba(255, 255, 255, 0.08)',
                          margin: '20px 0',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#fff',
                          backdropFilter: 'blur(8px)',
                        }}
                      >
                        Loading chat...
                      </div>
                    ) : null}
                  </div>
                  <BottomInputWrapper>
                    <DeepSearchInput
                      value={searchText}
                      onChange={(e: any) => setSearchText(e.target.value)}
                      onEnter={() => fetchResponse()}
                      textareaRef={textareaRef}
                      hasDiscussionStarted={chatData.length > 0}
                      variant="hero"
                      placeholder="Ask about permits, air quality, water rights, compliance..."
                      uploadAttachment={uploadAttachment}
                      filePreviews={filePreviews}
                      removeAttachment={removeAttachment}
                      context={context}
                    />
                    <HintText style={{ color: 'rgba(228, 228, 231, 0.75)' }}>
                      Browse authorizations above or describe what you need below.
                    </HintText>
                  </BottomInputWrapper>
                </ChatStageForeground>
              </HeroEmptyState>
            )}
          </ChatSection>

          {hasDocuments && (
            <DocumentsSection>
              <SectionTitle>
                <span role="img" aria-label="documents">📄</span>
                <span>Documents</span>
              </SectionTitle>
              <div style={{ flex: 1, overflowY: 'auto' }}>
                {files.map((file, index) => (
                  <DocumentCard key={index} onClick={() => window.open(file.url, '_blank')}>
                    <DocumentTitle>
                      <span role="img" aria-label="pdf">📄</span>
                      {file.name}
                    </DocumentTitle>
                    <DocumentDate>
                      {(() => { const d = parseDateFromUrl(file.url); return `${d.toLocaleDateString()} ${d.toLocaleTimeString()}`; })()}
                    </DocumentDate>
                  </DocumentCard>
                ))}
              </div>
            </DocumentsSection>
          )}
        </ContentRow>
      </div>
    </div>
  );
};

export default withAuthContext(PermitAgent);


